import {
  extractFailedCompanyResultsFromTasks,
  extractFailedResultsFromTasks,
  getBulkMessageJobStatus,
  TBulkMessageJobListItem,
  TBulkMessageJobType,
  TJobTask,
  useBulkMessageJobs,
} from '@commenda/api/job';
import {
  Badge,
  Button,
  ColumnGroup,
  CustomColumnDef,
  DataTable,
  LoadingIndicator,
  Progress,
  Tooltip,
} from '@commenda/kakashi';
import { useMemo, useState } from 'react';
import { useNotification } from '../../hooks/useNotification';

function formatDateTime(dateStr: string | null): string {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleString();
}

function sanitizeCsvCell(value: string): string {
  if (/^[=+\-@\t\r]/.test(value)) {
    return `'${  value}`;
  }
  return value;
}

function downloadBulkMessageFailuresCsv(tasks: TJobTask[], jobType: TBulkMessageJobListItem['jobType']) {
  const firstTaskMetadata = tasks[0]?.metadata as {
    message?: string;
  } | null;
  const sentMessage = firstTaskMetadata?.message ?? '';

  let headers: string[];
  let rows: string[][];

  if (jobType === 'SEND_BULK_GENERAL_THREADS_MESSAGE') {
    const failedResults = extractFailedCompanyResultsFromTasks(tasks);
    if (failedResults.length === 0) return;
    headers = ['Company ID', 'Message', 'Error Message'];
    rows = failedResults.map((result) => [
      String(result.companyId),
      sentMessage,
      result.error ?? 'Unknown error',
    ]);
  } else {
    const failedResults = extractFailedResultsFromTasks(tasks);
    if (failedResults.length === 0) return;
    headers = ['SR ID', 'Message', 'Error Message'];
    rows = failedResults.map((result) => [
      result.serviceRequestId,
      sentMessage,
      result.error ?? 'Unknown error',
    ]);
  }

  const csvContent = [headers, ...rows]
    .map((row) =>
      row.map((cell) => `"${sanitizeCsvCell(String(cell).replace(/"/g, '""'))}"`).join(',')
    )
    .join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `failed-bulk-messages-${new Date().toISOString().split('T')[0]}.csv`;
  document.body.appendChild(link);
  link.click();
  setTimeout(() => {
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, 100);
}

interface BulkMessageJobsTableProps {
  jobTypes?: TBulkMessageJobType[];
  emptyStateDescription: string;
}

export const BulkMessageJobsTable = ({
  jobTypes,
  emptyStateDescription,
}: BulkMessageJobsTableProps) => {
  const { data, isLoading } = useBulkMessageJobs(jobTypes);
  const { setNotification } = useNotification();
  const [downloadingJobId, setDownloadingJobId] = useState<string | null>(null);

  const handleDownloadFailures = async (jobId: string, jobType: TBulkMessageJobListItem['jobType']) => {
    setDownloadingJobId(jobId);
    try {
      const jobStatus = await getBulkMessageJobStatus(jobId);
      if (!jobStatus?.tasks) {
        setNotification({
          show: true,
          valid: false,
          title: 'Error',
          message: 'Failed to fetch job details for CSV download.',
        });
        return;
      }
      downloadBulkMessageFailuresCsv(jobStatus.tasks, jobType);
    } catch {
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message: 'Failed to download failure details.',
      });
    } finally {
      setDownloadingJobId(null);
    }
  };

  const columns: CustomColumnDef<TBulkMessageJobListItem, unknown>[] = useMemo(
    () => [
      {
        id: 'startedAt',
        accessorKey: 'startedAt',
        header: 'Started',
        cell: ({ row }) => (
          <span className="txt-small-regular whitespace-nowrap">
            {formatDateTime(row.original.startedAt)}
          </span>
        ),
      },
      {
        id: 'jobType',
        accessorKey: 'jobType',
        header: 'Type',
        cell: ({ row }) => {
          const isCompany = row.original.jobType === 'SEND_BULK_GENERAL_THREADS_MESSAGE';
          return (
            <Badge variant={isCompany ? 'blue' : 'gray'}>
              {isCompany ? 'General Threads' : 'Service Request'}
            </Badge>
          );
        },
      },
      {
        id: 'message',
        accessorKey: 'message',
        header: 'Message',
        cell: ({ row }) => {
          const msg = row.original.message ?? '';
          const truncated = msg.length > 50 ? `${msg.slice(0, 50)  }...` : msg;
          return (
            <Tooltip content={msg} disabled={msg.length <= 50} asChild>
              <span className="txt-small-regular">{truncated || '—'}</span>
            </Tooltip>
          );
        },
      },
      {
        id: 'progress',
        accessorKey: 'processedRows',
        header: 'Progress',
        cell: ({ row }) => {
          const processed = row.original.processedRows ?? 0;
          const total = row.original.totalRows ?? 0;
          const percent = total > 0 ? Math.round((processed / total) * 100) : 0;
          return (
            <div className="flex items-center gap-sm min-w-[120px]">
              <Progress value={percent} accent={row.original.isJobCompleted ? 'success' : 'blue'} className="flex-1 rounded-full" />
              <span className="txt-extra-small-medium text-text-tertiary whitespace-nowrap">
                {percent}%
              </span>
            </div>
          );
        },
      },
      {
        id: 'total',
        accessorKey: 'totalRows',
        header: 'Total',
        cell: ({ row }) => <span>{row.original.totalRows ?? 0}</span>,
      },
      {
        id: 'sent',
        accessorKey: 'successCount',
        header: 'Sent',
        cell: ({ row }) => (
          <span className="text-fg-success">{row.original.successCount ?? 0}</span>
        ),
      },
      {
        id: 'failed',
        accessorKey: 'failureCount',
        header: 'Failed',
        cell: ({ row }) => {
          const count = row.original.failureCount ?? 0;
          return (
            <span className={count > 0 ? 'text-text-error' : ''}>
              {count}
            </span>
          );
        },
      },
      {
        id: 'finishedAt',
        accessorKey: 'finishedAt',
        header: 'Completed',
        cell: ({ row }) => (
          <span className="txt-small-regular whitespace-nowrap">
            {formatDateTime(row.original.finishedAt)}
          </span>
        ),
      },
      {
        id: 'actions',
        accessorKey: 'id',
        header: 'Download',
        cell: ({ row }) => {
          const hasFailures = (row.original.failureCount ?? 0) > 0;
          const isDownloading = downloadingJobId === row.original.id;

          return (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => handleDownloadFailures(row.original.id, row.original.jobType)}
              disabled={!hasFailures || isDownloading}
            >
              {isDownloading ? (
                <LoadingIndicator size={16} />
              ) : (
                'Download Failures'
              )}
            </Button>
          );
        },
      },
    ],
    [downloadingJobId]
  );

  const columnGroups: ColumnGroup<TBulkMessageJobListItem, unknown>[] = useMemo(
    () => [{ id: 'main', columns }],
    [columns]
  );

  if (isLoading) {
    return (
      <div className="flex justify-center py-xl">
        <LoadingIndicator size={32} />
      </div>
    );
  }

  return (
    <div className="rounded-md border border-border-primary">
      <DataTable
        columnGroups={columnGroups}
        data={data?.jobs ?? []}
        emptyStateConfig={{
          title: 'No bulk message jobs',
          description: emptyStateDescription,
        }}
      />
    </div>
  );
};
